import { DAG_FLOW_ANCHOR_Y, DAG_FLOW_LABEL_WIDTH, getProjectDagActionsStart, isProjectDagFlowLabelBelow } from './projectDagSectionsLayout';
import { PROJECT_WORKSPACE_NODE_WIDTH } from './projectDagNodeDimensions';

// Visual membership is independent from execution order and parallel groups.
export const recoveryRevisionGroup = (action) => {
  const existing = normalizeActionLogicalGroup(action?.logical_group);
  if (existing) return existing;
  if (!/^\d+$/.test(String(action?.id || '')) || !action?.flow_id) return null;
  const id = Number(action.id);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const title = String(action.name || action.type || '').slice(0, 4000);
  return { id: id.toString(16).padStart(32, '0'), title, originalPrompt: title };
};

export const normalizeActionLogicalGroup = (value) => {
  let group = value;
  if (typeof group === 'string') {
    if (group.length > 25000) return null;
    try {
      group = JSON.parse(group);
    } catch (_error) {
      return null;
    }
  }
  if (!group || typeof group !== 'object' || Array.isArray(group)
    || typeof group.id !== 'string' || !group.id.trim() || group.id.length > 160
    || typeof group.title !== 'string' || !group.title.trim() || group.title.length > 4000) return null;
  const originalPrompt = group.original_prompt ?? group.originalPrompt ?? group.title;
  if (typeof originalPrompt !== 'string' || originalPrompt.length > 4000) return null;
  return { id: group.id, title: group.title, originalPrompt };
};

// A mixed flow belongs to a box at its entry when every connected action on
// both sides of the trigger belongs to that box. Later actions may differ.
const groupedFlowTriggers = (flowBranches, connections) => {
  const owners = new Map();
  const triggers = new Map();
  flowBranches.forEach((branch) => {
    const actions = (branch.nodes || []).filter((node) => node.nodeType === 'action');
    actions.forEach((node) => owners.set(node.key, normalizeActionLogicalGroup(node.logicalGroup)?.id));
    const trigger = (branch.nodes || []).find((node) => node.nodeType === 'flow');
    if (!trigger) return;
    const groupId = actions.length && owners.get(actions[0].key);
    if (groupId && actions.every((node) => owners.get(node.key) === groupId)) triggers.set(trigger.key, groupId);
  });
  const incoming = new Map();
  const outgoing = new Map();
  connections.forEach(({ source, target }) => {
    if (source !== 'center') {
      if (!incoming.has(target)) incoming.set(target, []);
      incoming.get(target).push(source);
    }
    if (!outgoing.has(source)) outgoing.set(source, []);
    outgoing.get(source).push(target);
  });
  flowBranches.forEach((branch) => {
    const trigger = (branch.nodes || []).find((node) => node.nodeType === 'flow');
    if (!trigger || triggers.has(trigger.key)) return;
    const before = incoming.get(trigger.key) || [];
    const after = outgoing.get(trigger.key) || [];
    const groupId = before.length && owners.get(before[0]);
    if (groupId && after.length && [...before, ...after].every((key) => owners.get(key) === groupId)) {
      triggers.set(trigger.key, groupId);
    }
  });
  return triggers;
};

// Execution order comes from connections, never from the user's canvas layout.
// The reported usage is the latest available usage of each action, which may
// correspond to different executions.
export const buildLogicalGroupTokenSummaries = (flowBranches, connections) => {
  const groups = new Map();
  const ownerByNode = new Map();
  const triggerGroups = groupedFlowTriggers(flowBranches, connections);
  flowBranches.forEach((branch) => {
    const actions = (branch.nodes || []).filter((node) => node.nodeType === 'action');
    actions.forEach((node) => {
      const groupId = normalizeActionLogicalGroup(node.logicalGroup)?.id;
      if (!groupId) return;
      const group = groups.get(groupId) || { actions: new Map(), triggers: new Set() };
      group.actions.set(node.key, node.item || {});
      groups.set(groupId, group);
      ownerByNode.set(node.key, groupId);
    });
    const trigger = (branch.nodes || []).find((node) => node.nodeType === 'flow');
    const groupId = trigger && triggerGroups.get(trigger.key);
    if (groupId && groups.has(groupId)) {
      groups.get(groupId).triggers.add(trigger.key);
      ownerByNode.set(trigger.key, groupId);
    }
  });

  const outgoing = new Map();
  connections.forEach(({ source, target }) => {
    const owner = ownerByNode.get(source);
    if (!owner || owner !== ownerByNode.get(target)) return;
    if (!outgoing.has(source)) outgoing.set(source, []);
    outgoing.get(source).push(target);
  });
  const summaries = new Map();
  groups.forEach(({ actions, triggers }, groupId) => {
    const hasPrevious = new Set();
    const hasNext = new Set();
    actions.forEach((_item, source) => {
      const pending = [...(outgoing.get(source) || [])];
      const visited = new Set();
      while (pending.length) {
        const target = pending.pop();
        if (visited.has(target)) continue;
        visited.add(target);
        if (actions.has(target)) {
          hasNext.add(source);
          hasPrevious.add(target);
        } else if (triggers.has(target)) {
          pending.push(...(outgoing.get(target) || []));
        }
      }
    });
    const first = [...actions.keys()].filter((key) => !hasPrevious.has(key));
    const last = [...actions.keys()].filter((key) => !hasNext.has(key));
    const count = (item, key) => {
      const usage = item?.aiTokenUsage || item?.ai_token_usage;
      const value = usage?.[key];
      const number = Number(value);
      return value !== null && value !== undefined && Number.isFinite(number) && number >= 0
        ? Math.round(number) : null;
    };
    let total = 0;
    let withUsage = 0;
    actions.forEach((item) => {
      const value = count(item, 'total_tokens');
      if (value !== null) { total += value; withUsage += 1; }
    });
    summaries.set(groupId, {
      input: first.length === 1 ? count(actions.get(first[0]), 'input_tokens') : null,
      output: last.length === 1 ? count(actions.get(last[0]), 'output_tokens') : null,
      total: withUsage ? total : null,
      partial: withUsage < actions.size,
      ambiguous: first.length !== 1 || last.length !== 1,
    });
  });
  return summaries;
};

const addNodeBounds = (bounds, node) => {
  if (!node || node.hidden) return;
  const { x, y } = node.position;
  const width = node.measured?.width ?? node.width ?? 154;
  const height = node.measured?.height ?? node.height ?? 150;
  if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return;
  bounds.left = Math.min(bounds.left, x);
  bounds.top = Math.min(bounds.top, y);
  bounds.right = Math.max(bounds.right, x + width);
  bounds.bottom = Math.max(bounds.bottom, y + height);
  bounds.count += 1;
};

export const buildActionLogicalGroupBounds = (nodes, nodeType = 'action', flowBranches = [], flowLabelHeights = {}, connections = []) => {
  const groups = new Map();
  nodes.forEach((node) => {
    const actionNode = node.data?.node;
    if (actionNode?.nodeType !== nodeType) return;
    const group = normalizeActionLogicalGroup(actionNode.logicalGroup);
    if (!group) return;
    const bounds = groups.get(group.id) || {
      ...group, count: 0, left: Number.POSITIVE_INFINITY, top: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY, bottom: Number.NEGATIVE_INFINITY, members: [], triggerKeys: [],
    };
    bounds.members.push({
      key: actionNode.key || node.id,
      label: actionNode.label || actionNode.item?.name || actionNode.item?.type || '',
      node: actionNode,
    });
    groups.set(group.id, bounds);
    addNodeBounds(bounds, node);
  });
  if (nodeType === 'action' && flowBranches.length) {
    const triggerGroups = groupedFlowTriggers(flowBranches, connections);
    const byId = new Map(nodes.map((node) => [node.id, node]));
    const center = byId.get('center');
    const sectionStart = center && getProjectDagActionsStart(center.position.x,
      center.data?.node?.width || PROJECT_WORKSPACE_NODE_WIDTH);
    flowBranches.forEach((branch) => {
      const triggerKey = branch.nodes.find((node) => node.nodeType === 'flow')?.key;
      const groupId = triggerGroups.get(triggerKey);
      const bounds = groupId && groups.get(groupId);
      if (!bounds || !bounds.count) return;
      const trigger = byId.get(triggerKey);
      if (!trigger || trigger.hidden) return;
      bounds.triggerKeys.push(trigger.id);
      addNodeBounds(bounds, trigger);
      const height = flowLabelHeights[trigger.id] || 64;
      const width = trigger.measured?.width ?? trigger.width ?? 154;
      const below = isProjectDagFlowLabelBelow(trigger.position.x, sectionStart);
      const x = trigger.position.x + (below ? (width - DAG_FLOW_LABEL_WIDTH) / 2 : 27 - DAG_FLOW_LABEL_WIDTH);
      const y = trigger.position.y + (below ? 100 : DAG_FLOW_ANCHOR_Y - height / 2);
      bounds.left = Math.min(bounds.left, x);
      bounds.top = Math.min(bounds.top, y);
      bounds.right = Math.max(bounds.right, x + DAG_FLOW_LABEL_WIDTH);
      bounds.bottom = Math.max(bounds.bottom, y + height);
    });
  }
  return Array.from(groups.values()).filter((group) => group.count > 0).map((group) => ({
    id: group.id, title: group.title, originalPrompt: group.originalPrompt, members: group.members,
    triggerKeys: group.triggerKeys, nodeKeys: [...group.members.map((member) => member.key), ...group.triggerKeys],
    x: group.left - 28, y: group.top - 54,
    width: group.right - group.left + 56, height: group.bottom - group.top + 78,
  }));
};

export const collapsedLogicalGroupSize = (group) => ({ width: Math.max(260, Math.min(400, group.width)), height: 70 });
export const logicalGroupAnchorId = (groupId) => `logical-group:${groupId}`;

// Collapse is a view projection only. Real positions, handles and execution
// connections remain intact for expanding, moving and saving the project.
export const buildCollapsedLogicalGroupView = (nodes, edges, groups, collapsedIds) => {
  if (!collapsedIds.size) return { nodes, edges };
  const memberGroups = new Map();
  const anchors = [];
  groups.forEach((group) => {
    if (!collapsedIds.has(group.id)) return;
    const id = logicalGroupAnchorId(group.id);
    (group.nodeKeys || group.members.map((member) => member.key)).forEach((key) => memberGroups.set(key, id));
    const size = collapsedLogicalGroupSize(group);
    anchors.push({ id, type: 'logicalGroupAnchor', position: { x: group.x, y: group.y },
      data: { title: group.title, helpNodeKeys: group.nodeKeys || group.members.map((member) => member.key) },
      ariaLabel: group.title, draggable: false, selectable: false, focusable: false,
      connectable: false, deletable: false, width: size.width, height: size.height,
      style: { ...size, pointerEvents: 'none' } });
  });
  if (!anchors.length) return { nodes, edges };
  return {
    nodes: [...nodes.map((node) => memberGroups.has(node.id) && !node.hidden ? { ...node, hidden: true } : node), ...anchors],
    edges: edges.map((edge) => {
      const source = memberGroups.get(edge.source);
      const target = memberGroups.get(edge.target);
      if (!source && !target) return edge;
      if (source && source === target) return { ...edge, hidden: true };
      return { ...edge, ...(source ? { source, sourceHandle: 'source' } : {}),
        ...(target ? { target, targetHandle: 'target' } : {}),
        // Flow names are rendered separately from the node. Hide that label
        // along with an absorbed trigger, keeping only the box title visible.
        ...(memberGroups.has(edge.data?.flowNode?.key) ? { data: { ...edge.data, flowLabel: undefined } } : {}) };
    }),
  };
};

// Clamp the translation as a whole so members keep their relative spacing.
// Only member positions are read from the drag-start snapshot; unrelated nodes
// retain their current state even if a runtime update arrives during the drag.
export const moveActionLogicalGroup = (nodes, startPositions, delta) => {
  let minX = -Infinity;
  let maxX = Infinity;
  nodes.forEach((node) => {
    const start = startPositions.get(node.id);
    if (!start) return;
    const width = node.measured?.width || node.width || 154;
    if (Array.isArray(node.extent)) {
      minX = Math.max(minX, node.extent[0][0] - start.x);
      maxX = Math.min(maxX, node.extent[1][0] - width - start.x);
    }
  });
  const x = Math.max(minX, Math.min(maxX, delta.x));
  return nodes.map((node) => {
    const start = startPositions.get(node.id);
    if (!start) return node;
    const position = { x: start.x + x, y: start.y + delta.y };
    return position.x === node.position.x && position.y === node.position.y ? node : { ...node, position };
  });
};
